import { useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { BaseLayout } from "../../shared/layouts/base-layout-page";
import { findFirstErrorMessage } from "../../shared/utils/form/find-first-error-message";
import { useTheme } from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import { ContatoFormPart } from "../../shared/components/formulario/parts/ContatoFormPart/contato-form.part";
import { EnderecoFormPart } from "../../shared/components/formulario/parts/EnderecoFormPart/enderecoform.part";
import { PessoaJuridicaFormPart } from "../../shared/components/formulario/parts/PessoaJuridicaFormPart/pessoa-juridica.part";
import { PessoaFisicaFormPart } from "../../shared/components/formulario/parts/PessoaFisicaFormPart/pessoa-fisica.part";
import { FerramanetasDetalhe } from "../../shared/components/ferramentasDaListagem/ferramentas-detalhe";
import { useMutation } from "@tanstack/react-query";
import useLoadingStore from "../../shared/store/loading.store";
import { toast } from "react-toastify";
import { ICnpjData } from "../../shared/components/buscarCNPJ/buscar-cnpj";
import { toastError } from "../../shared/services/errors/erros-handler";
import {
  EditarClienteFormData,
  editarClienteSchema,
} from "./schema/editar-empresa.schema";
import { clienteService } from "./service/cliente.service";

export function EditarCliente() {
  const methods = useForm<EditarClienteFormData>({
    resolver: yupResolver(editarClienteSchema),
    defaultValues: {
      active: true,
      cnpj: "",
      cpf: "",
    },
  });

  const {
    handleSubmit,
    setValue,
    control,
    setError,
    formState: { errors },
  } = methods;

  const navigate = useNavigate();
  const [valueOpcao, setOpicao] = useState("cnpj");
  const { setLoading, stopLoading } = useLoadingStore();
  const { id } = useParams<{ id: string }>();

  // Exibe o primeiro erro de validação quando o submit é bloqueado pelo schema
  const onInvalidSubmit = (formErrors: typeof errors) => {
    const primeiraMensagem = findFirstErrorMessage(formErrors);
    toast.error(primeiraMensagem || "Existem campos inválidos no formulário.");
  };

  useEffect(() => {
    if (id) {
      clienteService
        .detalhar(id)
        .then((cliente) => {
          methods.reset(cliente);
          setOpicao(cliente.cnpj ? "cnpj" : "cpf");
        })
        .catch((error) => {
          console.error("Erro ao carregar cliente:", error);
          toast.error("Erro ao carregar cliente.");
        });
    }
  }, [methods]);

  const { isPending, mutate } = useMutation({
    mutationFn: async (data: EditarClienteFormData) => {
      if (!id) {
        toast.error("ID do cliente não encontrado.");
        return;
      }
      setLoading(true, "Estamos quase lá! Só mais um momento...");
      await clienteService.editar(id, data);
    },
    onSuccess: () => {
      stopLoading();
      toast.success("Cliente editada com sucesso");
    },
    onError: (err: any) => {
      stopLoading();
      toast.error(err.mensagem || "Erro ao editar empresa.");
    },
  });

  const preencherCampos = (dados: ICnpjData) => {
    setValue("fantasyName", dados.fantasia || dados.razaoSocial || "");
    setValue("corporateName", dados.razaoSocial || "");
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOpicao(event.target.value);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Tem certeza que deseja excluir este cliente?")) {
      try {
        const empresaDeletada = await clienteService.deletar(id);
        toast.success(empresaDeletada);
        navigate("/empresa");
      } catch (error) {
        toastError(error);
      }
    }
  };

  const theme = useTheme();
  const mdDown = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <BaseLayout title="Editar Cliente">
      <FerramanetasDetalhe
        mostraBotaoApagar={true}
        aoClicarBotaoApagar={() => handleDelete(id || "")}
        mostraBotaoNovo={false}
        mostraBotaoSalvarEVoltar={true}
        aoClicarBotaoVoltarESalvar={handleSubmit(
          (formData) => mutate(formData),
          onInvalidSubmit
        )}
        mostraBotaoVoltar
        textoBotaoNovo="Adicionar Nova"
        aoClicarBotaoSalvar={handleSubmit((formData) => {
          mutate(formData);
          navigate("/cliente");
        }, onInvalidSubmit)}
        aoClicarBotaoVoltar={() => navigate("/cliente")}
      />
      <Box
        component="form"
        onSubmit={handleSubmit((formData) => {
          mutate(formData);
          navigate("/cliente");
        }, onInvalidSubmit)}
        padding="20px 0"
        display="flex"
        flexDirection="column"
        height="calc(100% - 100px )"
      >
        <Box padding="20px 0">
          <Typography variant="h5" padding="20px 0">
            Dados do Cliente:
          </Typography>
          <Box
            display="grid"
            gridTemplateColumns={mdDown ? "1fr" : "1fr 1fr"}
            gap="20px"
            width="100%"
            flexWrap="wrap"
          >
            <FormControl>
              <FormLabel>Selecione CPF ou CNPJ</FormLabel>
              <RadioGroup value={valueOpcao} onChange={handleChange} row>
                <FormControlLabel
                  value="cnpj"
                  control={<Radio />}
                  label="CNPJ"
                />
                <FormControlLabel value="cpf" control={<Radio />} label="CPF" />
              </RadioGroup>
            </FormControl>

            {valueOpcao === "cnpj" ? (
              <PessoaJuridicaFormPart
                methods={methods}
                preencherCampos={preencherCampos}
              />
            ) : (
              <PessoaFisicaFormPart methods={methods} mdDown={mdDown} />
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          <ContatoFormPart methods={methods} mdDown={mdDown} />

          <Divider sx={{ my: 2 }} />

          <EnderecoFormPart
            methods={methods}
            mdDown={mdDown}
          />

          <Divider sx={{ my: 2 }} />

          <Typography variant="h5" padding="20px 0">
            Status:
          </Typography>
          <Box
            display="grid"
            gridTemplateColumns={mdDown ? "1fr" : "1fr 1fr"}
            gap="20px"
            width="100%"
            flexWrap="wrap"
          >
            <Controller
              name="active"
              control={control}
              defaultValue={true}
              render={({ field }) => (
                <FormControl>
                  <FormLabel>Empresa ativada</FormLabel>
                  <RadioGroup
                    row
                    value={field.value ? "true" : "false"}
                    onChange={(e) => field.onChange(e.target.value === "true")}
                  >
                    <FormControlLabel
                      value="true"
                      control={<Radio />}
                      label="Ativa"
                    />
                    <FormControlLabel
                      value="false"
                      control={<Radio />}
                      label="Desativada"
                    />
                  </RadioGroup>
                </FormControl>
              )}
            />
          </Box>
        </Box>
      </Box>
    </BaseLayout>
  );
}
